'use client';

import React, { Suspense, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useToast } from '@/hooks/useToast';
import {
  fetchPRReviewsConfig,
  storePRReviewsConfig,
} from '@/apis/prReviewsConfig';
import {
  fetchLeaderboardVisibility,
  updateLeaderboardVisibility,
} from '@/apis/leaderboardVisibility';
import useUserStore from '@/state/userState';
import useOrganizationStore from '@/state/orgState';
import ExcludePatterns from '@/components/PullRequests/SettingsMenu/ExcludePatterns';

/** Brief chunk-fetch shell for non-default Code Review Settings sub-tabs. */
function PrSettingsSubTabSkeleton() {
  return (
    <div className="animate-pulse space-y-4 pt-2" aria-busy="true" aria-live="polite">
      <div className="h-8 w-48 rounded bg-ds-gray-subtlest" />
      <div className="h-4 w-full max-w-xl rounded bg-ds-gray-subtlest" />
      <div className="space-y-3 rounded-lg border border-ds-border-subtlest p-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-10 w-full rounded bg-ds-gray-subtlest" />
        ))}
      </div>
    </div>
  );
}

// Code-split only. Default tab ("Settings") stays static; these three mount on
// sub-tab change and have no FE-11 SSR hazard, so they still prerender when selected.
const PRGuidelines = dynamic(
  () => import('@/components/PullRequests/Guidelines'),
  { loading: PrSettingsSubTabSkeleton }
);
const PRLearnings = dynamic(
  () => import('@/components/PullRequests/Learnings'),
  { loading: PrSettingsSubTabSkeleton }
);
const RepoDependenciesTab = dynamic(
  () => import('@/components/Settings/tabs/RepoDependenciesTab'),
  { loading: PrSettingsSubTabSkeleton }
);

interface SettingsType {
  title: string;
  description: string;
  name: keyof PRSettings;
  value: string | boolean | number;
}

interface PRSettings {
  priorityLevel: string;
  mode: string;
  highLevelSummary: boolean;
  walkThrough: boolean;
  changedFilesSummary: boolean;
  autoTitle: boolean;
  autoLinkTicket: boolean;
  learningsCrossRepo: boolean;
  enableLGTM: boolean;
  enableDiagram: boolean;
  static_scan_enabled: boolean;
  pr_metrics_enabled: boolean;
  pr_review_enabled: boolean;
  pr_scoring_enabled: boolean;
  review_on_every_push: boolean;
  auto_resolve_on_reply: boolean;
  comment_style: string;
  show_confidence_score: boolean;
  reply_to_other_bots_comments: boolean;
  auto_approve_on_safe: boolean;
  max_comments: number;
}

interface SettingsPageProps {
  hideTabs?: boolean;
}

function SettingsSettingRow({
  setting,
  onChange,
  isConfigLoaded = true,
}: {
  setting: SettingsType;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => void;
  isConfigLoaded?: boolean;
}) {
  const descRef = React.useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  // Numeric fields (e.g. max_comments) keep a local draft so the user can type
  // freely; the value is only committed (and saved) on blur/Enter rather than
  // on every keystroke. Keep the draft in sync when the backing value changes
  // (e.g. after the initial fetch populates settings).
  const [numberDraft, setNumberDraft] = React.useState<string>(
    typeof setting.value === 'number' ? String(setting.value) : ''
  );
  React.useEffect(() => {
    if (typeof setting.value === 'number') {
      setNumberDraft(String(setting.value));
    }
  }, [setting.value]);

  const commitNumber = () => {
    // Clamp to a sane floor of 1 — the backend also clamps, but mirror it here
    // so the UI never shows/saves a value the bot would silently reject.
    const parsed = Math.max(1, Math.floor(Number(numberDraft)));
    const next = Number.isFinite(parsed) ? parsed : 1;
    setNumberDraft(String(next));
    onChange({
      target: { name: setting.name, value: String(next), checked: false },
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  React.useEffect(() => {
    const el = descRef.current;
    if (!el) return;

    const check = () => setIsTruncated(el.scrollWidth > el.clientWidth);

    const rafId = requestAnimationFrame(check);
    const observer = new ResizeObserver(check);
    observer.observe(el);

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [setting.description]);

  return (
    <React.Fragment key={setting.name}>
      <tr className="border-t border-ds-border-subtlest hover:bg-primitive-gray-50 transition-colors">
        <td className="p-4">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <div className="font-medium text-ds-text-darker flex items-center gap-2">
                {setting.title}
                {(isTruncated || setting.name === 'priorityLevel') && (
                  <TooltipProvider delay={0}>
                    <Tooltip>
                      <TooltipTrigger render={
                        <button
                          type="button"
                          className="cursor-pointer inline-flex p-0.5 rounded"
                          aria-label="Scoring info"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="15"
                            height="14"
                            viewBox="0 0 15 14"
                            fill="none"
                            className="text-ds-text-subtle"
                          >
                            <path
                              d="M7.4974 12.8334C10.7191 12.8334 13.3307 10.2217 13.3307 7.00002C13.3307 3.77836 10.7191 1.16669 7.4974 1.16669C4.27573 1.16669 1.66406 3.77836 1.66406 7.00002C1.66406 10.2217 4.27573 12.8334 7.4974 12.8334Z"
                              stroke="currentColor"
                              strokeWidth="1.16667"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M7.5 9.33333V7"
                              stroke="currentColor"
                              strokeWidth="1.16667"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M7.5 4.66669H7.50583"
                              stroke="currentColor"
                              strokeWidth="1.16667"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      } />
                      <TooltipContent
                        side="left"
                        className="max-w-[500px] p-4 text-sm bg-ds-surface-white text-ds-text-darker border border-ds-border-subtlest shadow-lg"
                      >
                        <p className="mb-3 text-ds-text-default">
                          {setting.description}
                        </p>
                        {setting.name === 'priorityLevel' && (
                          <div className="space-y-3">
                            <p className="font-semibold text-ds-text-darker">
                              Scoring System:
                            </p>
                            <div className="mb-3">
                              <p className="text-xs font-semibold mb-1 text-ds-text-darker">
                                1. Production Impact Score (1-5):
                              </p>
                              <p className="text-xs text-ds-text-default mb-1">
                                • 5: Will definitely cause production
                                crashes/failures
                              </p>
                              <p className="text-xs text-ds-text-default mb-1">
                                • 4: High likelihood of production issues
                              </p>
                              <p className="text-xs text-ds-text-default mb-1">
                                • 3: Moderate chance of production problems
                              </p>
                              <p className="text-xs text-ds-text-default mb-1">
                                • 2: Low probability of production impact
                              </p>
                              <p className="text-xs text-ds-text-default">
                                • 1: Minimal/no production impact
                              </p>
                            </div>
                            <div className="mb-3">
                              <p className="text-xs font-semibold mb-1 text-ds-text-darker">
                                2. Fix Specificity Score (1-5):
                              </p>
                              <p className="text-xs text-ds-text-default mb-1">
                                • 5: Clear, direct, immediately applicable fix
                              </p>
                              <p className="text-xs text-ds-text-default mb-1">
                                • 4: Specific fix with minor adaptations needed
                              </p>
                              <p className="text-xs text-ds-text-default mb-1">
                                • 3: General approach to fix provided
                              </p>
                              <p className="text-xs text-ds-text-default mb-1">
                                • 2: Vague suggestion for improvement
                              </p>
                              <p className="text-xs text-ds-text-default">
                                • 1: No clear fix direction
                              </p>
                            </div>
                            <div className="mb-3">
                              <p className="text-xs font-semibold mb-1 text-ds-text-darker">
                                3. Urgency Impact Score (1-5):
                              </p>
                              <p className="text-xs text-ds-text-default mb-1">
                                • 5: Critical - Must be fixed immediately
                              </p>
                              <p className="text-xs text-ds-text-default mb-1">
                                • 4: High - Should be fixed very soon
                              </p>
                              <p className="text-xs text-ds-text-default mb-1">
                                • 3: Medium - Should be fixed in near term
                              </p>
                              <p className="text-xs text-ds-text-default mb-1">
                                • 2: Low - Can be fixed later
                              </p>
                              <p className="text-xs text-ds-text-default">
                                • 1: Very Low - Nice to have
                              </p>
                            </div>
                            <div className="pt-2 border-t border-ds-border-subtlest">
                              <p className="text-xs text-ds-text-default">
                                <strong className="text-ds-text-darker">
                                  Recommendation:
                                </strong>{' '}
                                Start with Critical issues (score &gt; 14) and
                                gradually work your way down as you become more
                                comfortable with the code review process. This
                                helps our AI learn your best practices over
                                time.
                              </p>
                            </div>
                          </div>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </div>
          </div>
        </td>
        <td className="p-4">
          <div
            ref={descRef}
            className="text-sm text-ds-text-subtle truncate max-w-[290px]"
          >
            {setting.description}
          </div>
        </td>
        <td className="p-4">
          <div className="flex items-center justify-end gap-8">
            {setting.name === 'priorityLevel' ? (
              <Select
                value={setting.value as string}
                onValueChange={(val) =>
                  onChange({
                    target: {
                      name: setting.name,
                      value: val,
                      checked: false,
                    },
                  } as unknown as React.ChangeEvent<HTMLSelectElement>)
                }
                items={{
                  critical: 'Critical issues only (Score > 14)',
                  medium: 'Medium priority and above (Score > 12)',
                  low: 'All issues (Score > 10)',
                }}
              >
                <SelectTrigger className="w-[200px] text-ds-text-darker bg-ds-surface-white rounded-lg h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="critical">
                    Critical issues only (Score &gt; 14)
                  </SelectItem>
                  <SelectItem value="medium">
                    Medium priority and above (Score &gt; 12)
                  </SelectItem>
                  <SelectItem value="low">
                    All issues (Score &gt; 10)
                  </SelectItem>
                </SelectContent>
              </Select>
            ) : setting.name === 'mode' ? (
              <Select
                value={setting.value as string}
                onValueChange={(val) =>
                  onChange({
                    target: {
                      name: setting.name,
                      value: val,
                      checked: false,
                    },
                  } as unknown as React.ChangeEvent<HTMLSelectElement>)
                }
                items={{
                  concise: 'Concise',
                  verbose: 'Verbose',
                }}
              >
                <SelectTrigger className="w-[200px] text-ds-text-darker bg-ds-surface-white rounded-lg h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="concise">Concise</SelectItem>
                  <SelectItem value="verbose">Verbose</SelectItem>
                </SelectContent>
              </Select>
            ) : setting.name === 'comment_style' ? (
              <Select
                value={setting.value as string}
                onValueChange={(val) =>
                  onChange({
                    target: {
                      name: setting.name,
                      value: val,
                      checked: false,
                    },
                  } as unknown as React.ChangeEvent<HTMLSelectElement>)
                }
                items={{
                  inline: 'Inline comments',
                  summary: 'Single summary comment',
                }}
              >
                <SelectTrigger className="w-[200px] text-ds-text-darker bg-ds-surface-white rounded-lg h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inline">Inline comments</SelectItem>
                  <SelectItem value="summary">
                    Single summary comment
                  </SelectItem>
                </SelectContent>
              </Select>
            ) : setting.name === 'max_comments' ? (
              isConfigLoaded ? (
                <Input
                  type="number"
                  min={1}
                  step={1}
                  value={numberDraft}
                  onChange={(e) => setNumberDraft(e.target.value)}
                  onBlur={commitNumber}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.currentTarget.blur();
                    }
                  }}
                  className="w-full text-ds-text-darker bg-ds-surface-white rounded-lg h-9 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
              ) : (
                <div className="w-full h-9 rounded-lg bg-primitive-gray-100 animate-pulse" />
              )
            ) : (
              <Switch
                checked={setting.value as boolean}
                onCheckedChange={(checked) =>
                  onChange({
                    target: {
                      name: setting.name,
                      value: '',
                      checked,
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              />
            )}
          </div>
        </td>
      </tr>
    </React.Fragment>
  );
}

function GeneralSettingsContent({
  allSettings,
  prReviewerSettings,
  contentGenerationSettings,
  automationSettings,
  analysisMetricsSettings,
  learningSettings,
  onSettingChange,
  isConfigLoaded,
}: {
  allSettings: SettingsType[];
  prReviewerSettings: readonly string[];
  contentGenerationSettings: readonly string[];
  automationSettings: readonly string[];
  analysisMetricsSettings: readonly string[];
  learningSettings: readonly string[];
  onSettingChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => void;
  isConfigLoaded: boolean;
}) {
  const prReviewerSettingsSet = new Set(prReviewerSettings);
  const contentGenerationSettingsSet = new Set(contentGenerationSettings);
  const automationSettingsSet = new Set(automationSettings);
  const analysisMetricsSettingsSet = new Set(analysisMetricsSettings);
  const learningSettingsSet = new Set(learningSettings);

  return (
    <div className="space-y-8">
      <div>
        <div className="py-4">
          <h3 className="text-lg font-semibold text-ds-text-darker mb-1">
            PR Reviewer Configuration
          </h3>
          <p className="text-sm text-ds-text-subtle">
            Configure how the AI PR reviewer behaves during code reviews
          </p>
        </div>
        <div className="border border-ds-border-subtlest rounded-xl overflow-hidden bg-ds-surface-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] table-fixed">
              <thead className="bg-primitive-gray-50  border-b border-ds-border-subtlest">
                <tr>
                  <th
                    className="text-left px-4 py-2 font-medium w-2/5"
                    style={{
                      color: 'var(--primitive-gray-500)',
                      fontSize: '14px',
                    }}
                  >
                    Setting Name
                  </th>
                  <th
                    className="text-left px-4 py-2 font-medium w-2/5"
                    style={{
                      color: 'var(--primitive-gray-500)',
                      fontSize: '14px',
                    }}
                  >
                    Description
                  </th>
                  <th className="w-1/5"></th>
                </tr>
              </thead>
              <tbody>
                {allSettings.reduce<React.ReactNode[]>((rows, setting) => {
                  if (prReviewerSettingsSet.has(setting.name)) {
                    rows.push(
                      <SettingsSettingRow
                        key={setting.name}
                        setting={setting}
                        onChange={onSettingChange}
                        isConfigLoaded={isConfigLoaded}
                      />
                    );
                  }
                  return rows;
                }, [])}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div>
        <div className="py-4">
          <h3 className="text-lg font-semibold text-ds-text-darker mb-1">
            Automated Content Generation
          </h3>
          <p className="text-sm text-ds-text-subtle">
            Control what content AI automatically generates
          </p>
        </div>
        <div className="border border-ds-border-subtlest rounded-xl overflow-hidden bg-ds-surface-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] table-fixed">
              <thead className="bg-primitive-gray-50  border-b border-ds-border-subtlest">
                <tr>
                  <th
                    className="text-left px-4 py-2 font-medium w-2/5"
                    style={{
                      color: 'var(--primitive-gray-500)',
                      fontSize: '14px',
                    }}
                  >
                    Setting Name
                  </th>
                  <th
                    className="text-left px-4 py-2 font-medium w-2/5"
                    style={{
                      color: 'var(--primitive-gray-500)',
                      fontSize: '14px',
                    }}
                  >
                    Description
                  </th>
                  <th className="w-1/5"></th>
                </tr>
              </thead>
              <tbody>
                {allSettings.reduce<React.ReactNode[]>((rows, setting) => {
                  if (contentGenerationSettingsSet.has(setting.name)) {
                    rows.push(
                      <SettingsSettingRow
                        key={setting.name}
                        setting={setting}
                        onChange={onSettingChange}
                      />
                    );
                  }
                  return rows;
                }, [])}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div>
        <div className="py-4">
          <h3 className="text-lg font-semibold text-ds-text-darker mb-1">
            Automation
          </h3>
          <p className="text-sm text-ds-text-subtle">
            Configure automated actions and generated assistance
          </p>
        </div>
        <div className="border border-ds-border-subtlest rounded-xl overflow-hidden bg-ds-surface-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] table-fixed">
              <thead className="bg-primitive-gray-50  border-b border-ds-border-subtlest">
                <tr>
                  <th
                    className="text-left px-4 py-2 font-medium w-2/5"
                    style={{
                      color: 'var(--primitive-gray-500)',
                      fontSize: '14px',
                    }}
                  >
                    Setting Name
                  </th>
                  <th
                    className="text-left px-4 py-2 font-medium w-2/5"
                    style={{
                      color: 'var(--primitive-gray-500)',
                      fontSize: '14px',
                    }}
                  >
                    Description
                  </th>
                  <th className="w-1/5"></th>
                </tr>
              </thead>
              <tbody>
                {allSettings.reduce<React.ReactNode[]>((rows, setting) => {
                  if (automationSettingsSet.has(setting.name)) {
                    rows.push(
                      <SettingsSettingRow
                        key={setting.name}
                        setting={setting}
                        onChange={onSettingChange}
                      />
                    );
                  }
                  return rows;
                }, [])}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div>
        <div className="py-4">
          <h3 className="text-lg font-semibold text-ds-text-darker mb-1">
            Analysis & Metrics
          </h3>
          <p className="text-sm text-ds-text-subtle">
            Enable scanning, scoring, and PR analytics
          </p>
        </div>
        <div className="border border-ds-border-subtlest rounded-xl overflow-hidden bg-ds-surface-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] table-fixed">
              <thead className="bg-primitive-gray-50  border-b border-ds-border-subtlest">
                <tr>
                  <th
                    className="text-left px-4 py-2 font-medium w-2/5"
                    style={{
                      color: 'var(--primitive-gray-500)',
                      fontSize: '14px',
                    }}
                  >
                    Setting Name
                  </th>
                  <th
                    className="text-left px-4 py-2 font-medium w-2/5"
                    style={{
                      color: 'var(--primitive-gray-500)',
                      fontSize: '14px',
                    }}
                  >
                    Description
                  </th>
                  <th className="w-1/5"></th>
                </tr>
              </thead>
              <tbody>
                {allSettings.reduce<React.ReactNode[]>((rows, setting) => {
                  if (analysisMetricsSettingsSet.has(setting.name)) {
                    rows.push(
                      <SettingsSettingRow
                        key={setting.name}
                        setting={setting}
                        onChange={onSettingChange}
                      />
                    );
                  }
                  return rows;
                }, [])}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div>
        <div className="py-4">
          <h3 className="text-lg font-semibold text-ds-text-darker mb-1">
            Learning Scope
          </h3>
          <p className="text-sm text-ds-text-subtle">
            Control how learnings are shared across repositories
          </p>
        </div>
        <div className="border border-ds-border-subtlest rounded-xl overflow-hidden bg-ds-surface-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] table-fixed">
              <thead className="bg-primitive-gray-50  border-b border-ds-border-subtlest">
                <tr>
                  <th
                    className="text-left px-4 py-2 font-medium w-2/5"
                    style={{
                      color: 'var(--primitive-gray-500)',
                      fontSize: '14px',
                    }}
                  >
                    Setting Name
                  </th>
                  <th
                    className="text-left px-4 py-2 font-medium w-2/5"
                    style={{
                      color: 'var(--primitive-gray-500)',
                      fontSize: '14px',
                    }}
                  >
                    Description
                  </th>
                  <th className="w-1/5"></th>
                </tr>
              </thead>
              <tbody>
                {allSettings.reduce<React.ReactNode[]>((rows, setting) => {
                  if (learningSettingsSet.has(setting.name)) {
                    rows.push(
                      <SettingsSettingRow
                        key={setting.name}
                        setting={setting}
                        onChange={onSettingChange}
                      />
                    );
                  }
                  return rows;
                }, [])}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function LeaderboardPrivacySection({
  leaderboardEnabled,
  loading,
  onToggle,
}: {
  leaderboardEnabled: boolean;
  loading: boolean;
  onToggle: (checked: boolean) => void;
}) {
  return (
    <div className="mt-8">
      <div className="py-4">
        <h3 className="text-lg font-semibold text-ds-text-darker mb-1">
          Leaderboard Privacy
        </h3>
        <p className="text-sm text-ds-text-subtle">
          Control whether your organization and engineers appear on the global
          leaderboard
        </p>
      </div>
      <div className="border border-ds-border-subtlest rounded-xl overflow-hidden bg-ds-surface-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px] table-fixed">
            <thead className="bg-primitive-gray-50 border-b border-ds-border-subtlest">
              <tr>
                <th
                  className="text-left px-4 py-2 font-medium w-2/5"
                  style={{
                    color: 'var(--primitive-gray-500)',
                    fontSize: '14px',
                  }}
                >
                  Setting Name
                </th>
                <th
                  className="text-left px-4 py-2 font-medium w-2/5"
                  style={{
                    color: 'var(--primitive-gray-500)',
                    fontSize: '14px',
                  }}
                >
                  Description
                </th>
                <th className="w-1/5"></th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-ds-border-subtlest hover:bg-primitive-gray-50 transition-colors">
                <td className="p-4">
                  <div className="font-medium text-ds-text-darker">
                    Show on Global Leaderboard
                  </div>
                </td>
                <td className="p-4">
                  <div className="text-sm text-ds-text-subtle truncate max-w-[290px]">
                    When enabled, your organization and engineers appear on the
                    public global leaderboard
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-end gap-8">
                    <Switch
                      checked={leaderboardEnabled}
                      disabled={loading}
                      onCheckedChange={onToggle}
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const SettingsPageContent: React.FC<SettingsPageProps> = ({
  hideTabs = false,
}) => {
  const showToast = useToast();
  const queryClient = useQueryClient();
  const searchParams = useSearchParams();
  const [tabIndex, setTabIndex] = useState(0);
  const [leaderboardEnabled, setLeaderboardEnabled] = useState(true);
  // False until the org's saved config has been fetched. Used to render a
  // skeleton for the numeric field (max_comments) so it never flashes the
  // hardcoded default (10) before the real value arrives.
  const [configLoaded, setConfigLoaded] = useState(false);

  // Handle URL parameter for sub-tab
  useEffect(() => {
    let index = 0;
    if (!hideTabs) {
      const subtab = searchParams?.get('subtab');
      if (subtab === 'codebase-context') index = 3;
      else if (subtab === 'guidelines') index = 1;
      else if (subtab === 'learnings') index = 2;
    }
    setTabIndex(index);
  }, [searchParams, hideTabs]);

  const [allSettings, setAllSettings] = useState<SettingsType[]>([
    {
      title: 'Comment Verbosity Controls',
      description: 'Set the verbosity level for code review comments',
      name: 'priorityLevel',
      value: 'critical',
    },
    {
      title: 'Mode',
      description: 'Set the mode for the comments',
      name: 'mode',
      value: 'concise',
    },
    {
      title: 'Comment Style',
      description:
        'How review findings are posted: inline comments on each line, or a single summary comment listing every finding',
      name: 'comment_style',
      value: 'inline',
    },
    {
      title: 'Show Confidence Score',
      description: 'Include the 1-5 confidence score in the PR review summary',
      name: 'show_confidence_score',
      value: true,
    },
    {
      title: 'Auto-approve at 5/5',
      description:
        'Submit a GitHub approval (not merge) when the review scores 5/5 — Safe to Merge',
      name: 'auto_approve_on_safe',
      value: false,
    },
    {
      title: 'Max Inline Comments',
      description:
        'Maximum number of inline review comments the bot posts per review. Defaults to 10.',
      name: 'max_comments',
      value: 10,
    },
    {
      title: 'High Level Summary',
      description: 'Generate high level summary of changes in PR description',
      name: 'highLevelSummary',
      value: false,
    },
    {
      title: 'Walkthrough',
      description: 'Generate a walkthrough',
      name: 'walkThrough',
      value: false,
    },
    {
      title: 'Changed Files Summary',
      description: 'Generate summary of the changed files',
      name: 'changedFilesSummary',
      value: false,
    },
    {
      title: 'Auto Generate Title',
      description: 'Automatically generate titles',
      name: 'autoTitle',
      value: false,
    },
    {
      title: 'Auto Link Ticket',
      description:
        'Automatically find the linked Jira or Linear ticket for each PR and include a scope coverage assessment in the review',
      name: 'autoLinkTicket',
      value: false,
    },
    {
      title: 'Static Scan Enabled',
      description: 'Enable static security analysis with SonarQube',
      name: 'static_scan_enabled',
      value: true,
    },
    {
      title: 'PR Metrics Enabled',
      description: 'Enable PR metrics collection and analysis',
      name: 'pr_metrics_enabled',
      value: false,
    },
    {
      title: 'PR Review Enabled',
      description: 'Enable automated PR review functionality',
      name: 'pr_review_enabled',
      value: false,
    },
    {
      title: 'PR Quality Scoring Enabled',
      description: 'Enable PR quality scoring and tagging functionality',
      name: 'pr_scoring_enabled',
      value: true,
    },
    {
      title: 'Auto Review on Every Commit',
      description: 'Trigger a new review on every commit push to a PR',
      name: 'review_on_every_push',
      value: true,
    },
    {
      title: 'Auto-Resolve Threads on Acceptance',
      description:
        'When the bot accepts a comment reply, automatically resolve the GitHub review thread',
      name: 'auto_resolve_on_reply',
      value: false,
    },
    {
      title: "Reply to Other Bots' Comments",
      description:
        'After our review, reply to comments from other review bots (CodeRabbit, Cursor, Greptile, Claude, Codex) stating whether their suggestion holds up against our analysis',
      name: 'reply_to_other_bots_comments',
      value: false,
    },
    {
      title: 'Enable LGTM Comments',
      description: 'Add LGTM (Looks Good To Me) 👍 comments on clean code',
      name: 'enableLGTM',
      value: false,
    },
    {
      title: 'Enable Sequence Diagram',
      description: 'Generate sequence diagrams for code changes',
      name: 'enableDiagram',
      value: false,
    },
    {
      title: 'Cross Repositories Learnings',
      description: 'Enable learnings cross repositories',
      name: 'learningsCrossRepo',
      value: false,
    },
  ]);

  const handleChange = async (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target as HTMLInputElement;
    const checked = (e.target as HTMLInputElement).checked;
    const newSettings = allSettings.map((setting) => {
      if (setting.name === name) {
        return {
          ...setting,
          value:
            name === 'priorityLevel' ||
            name === 'mode' ||
            name === 'comment_style'
              ? value
              : name === 'max_comments'
                ? Math.max(1, Math.floor(Number(value) || 1))
                : checked,
        };
      }
      return setting;
    });

    setAllSettings(newSettings);

    // Auto-save immediately
    await saveSettings(newSettings);
  };

  const saveSettings = (settingsToSave: SettingsType[]) => {
    if (!userUUID || !orgUUID) return;

    const mappedSettings = settingsToSave.reduce<PRSettings>(
      (acc, setting) => {
        if (setting.name === 'priorityLevel') {
          const priorityMap: Record<string, string> = {
            critical: 'high',
            medium: 'medium',
            low: 'low',
          };
          acc[setting.name] = priorityMap[setting.value as string] || 'high';
        } else if (
          setting.name === 'mode' ||
          setting.name === 'comment_style'
        ) {
          acc[setting.name] = setting.value as string;
        } else if (setting.name === 'max_comments') {
          acc[setting.name] = Math.max(
            1,
            Math.floor(Number(setting.value) || 1)
          );
        } else {
          acc[setting.name] = setting.value as boolean;
        }
        return acc;
      },
      {
        priorityLevel: 'high',
        mode: 'concise',
        highLevelSummary: false,
        walkThrough: false,
        changedFilesSummary: false,
        autoTitle: false,
        autoLinkTicket: false,
        learningsCrossRepo: false,
        enableLGTM: false,
        enableDiagram: false,
        static_scan_enabled: true,
        pr_metrics_enabled: true,
        pr_review_enabled: true,
        pr_scoring_enabled: true,
        review_on_every_push: true,
        auto_resolve_on_reply: false,
        comment_style: 'inline',
        show_confidence_score: true,
        reply_to_other_bots_comments: false,
        auto_approve_on_safe: false,
        max_comments: 10,
      }
    );

    configMutation.mutate({
      userUUID: userUUID as string,
      orgUUID: orgUUID as string,
      settings: mappedSettings,
    });
  };
  const userUUID = useUserStore((state) => state.user?.UserUUID);
  const orgUUIDFromStore = useOrganizationStore(
    (state) => state.organization?.OrgUUID
  );
  const orgUUIDFromUser = useUserStore((state) => state.user?.OrganizationUUID);
  const orgUUID = orgUUIDFromStore || orgUUIDFromUser;

  const configMutation = useMutation({
    mutationFn: storePRReviewsConfig,
    onSuccess: (storeResult) => {
      if (storeResult.error === null) {
        showToast({
          title: 'Settings saved',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      } else {
        showToast({
          title: 'Error saving settings',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    },
    onError: () => {
      showToast({
        title: 'Error saving settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    },
  });

  const leaderboardVisibilityQuery = useQuery({
    queryKey: ['leaderboardVisibility', orgUUID],
    queryFn: () => fetchLeaderboardVisibility({ OrgUUID: orgUUID as string }),
    enabled: !!orgUUID,
  });

  useEffect(() => {
    const data = leaderboardVisibilityQuery.data;
    if (data && data.Error === null) {
      setLeaderboardEnabled(data.LeaderboardEnabled);
    }
  }, [leaderboardVisibilityQuery.data]);

  const leaderboardMutation = useMutation({
    mutationFn: (checked: boolean) =>
      updateLeaderboardVisibility({
        OrgUUID: orgUUID as string,
        LeaderboardEnabled: checked,
      }),
    onMutate: (checked: boolean) => {
      const previousEnabled = leaderboardEnabled;
      setLeaderboardEnabled(checked);
      return { previousEnabled };
    },
    onSuccess: (res, checked, context) => {
      if (res.Error !== null) {
        setLeaderboardEnabled(context?.previousEnabled ?? !checked);
        showToast({
          title: 'Error updating leaderboard visibility',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } else {
        queryClient.invalidateQueries({ queryKey: ['recent-prs'] });
        showToast({
          title: 'Leaderboard visibility updated',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      }
    },
    onError: (_error, checked, context) => {
      setLeaderboardEnabled(context?.previousEnabled ?? !checked);
      showToast({
        title: 'Error updating leaderboard visibility',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    },
  });

  const handleLeaderboardToggle = (checked: boolean) => {
    if (!orgUUID || leaderboardMutation.isPending) return;
    leaderboardMutation.mutate(checked);
  };

  const prReviewsConfigQuery = useQuery({
    queryKey: ['prReviewsConfig', orgUUID, userUUID],
    queryFn: () => fetchPRReviewsConfig(userUUID as string, orgUUID),
    enabled: !!userUUID && !!orgUUID,
  });

  useEffect(() => {
    const data = prReviewsConfigQuery.data;
    if (!data) return;

    if (data.error === null) {
      const backendToFrontendPriority: Record<string, string> = {
        high: 'critical',
        medium: 'medium',
        low: 'low',
      };
      setAllSettings((allSettings) =>
        allSettings.map((setting) => {
          if (setting.name === 'priorityLevel') {
            return {
              ...setting,
              value:
                backendToFrontendPriority[data[setting.name] as string] ??
                'critical',
            };
          }
          if (setting.name === 'mode' || setting.name === 'comment_style') {
            return {
              ...setting,
              value: data[setting.name] || setting.value,
            };
          }
          if (setting.name === 'max_comments') {
            return {
              ...setting,
              value: data[setting.name] ?? setting.value,
            };
          }
          return {
            ...setting,
            value: data[setting.name] || false,
          };
        })
      );
    } else {
      showToast({
        title: 'Error fetching settings,please try again',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
    setConfigLoaded(true);
  }, [prReviewsConfigQuery.data, showToast]);

  const contentGenerationSettings = [
    'highLevelSummary',
    'walkThrough',
    'changedFilesSummary',
  ];

  const prReviewerSettings = [
    'pr_review_enabled',
    'review_on_every_push',
    'auto_resolve_on_reply',
    'reply_to_other_bots_comments',
    'priorityLevel',
    'mode',
    'comment_style',
    'show_confidence_score',
    'auto_approve_on_safe',
    'max_comments',
  ];

  const automationSettings = [
    'autoTitle',
    'autoLinkTicket',
    'enableLGTM',
    'enableDiagram',
  ];

  const analysisMetricsSettings = [
    'static_scan_enabled',
    'pr_metrics_enabled',
    'pr_scoring_enabled',
  ];

  const learningSettings = ['learningsCrossRepo'];

  return (
    <div
      className={
        hideTabs ? 'p-4 rounded-2xl bg-transparent' : 'bg-ds-surface-white'
      }
    >
      {hideTabs ? (
        <>
          <GeneralSettingsContent
            allSettings={allSettings}
            prReviewerSettings={prReviewerSettings}
            contentGenerationSettings={contentGenerationSettings}
            automationSettings={automationSettings}
            analysisMetricsSettings={analysisMetricsSettings}
            learningSettings={learningSettings}
            onSettingChange={handleChange}
            isConfigLoaded={configLoaded}
          />
          <LeaderboardPrivacySection
            leaderboardEnabled={leaderboardEnabled}
            loading={leaderboardMutation.isPending}
            onToggle={handleLeaderboardToggle}
          />
          {orgUUID && <ExcludePatterns orgUUID={orgUUID} />}
        </>
      ) : (
        <Tabs
          value={String(tabIndex)}
          onValueChange={(v) => setTabIndex(Number(v))}
          className="w-full"
        >
          <TabsList>
            <TabsTrigger value="0">Settings</TabsTrigger>
            <TabsTrigger value="1">Guidelines</TabsTrigger>
            <TabsTrigger value="2">Learnings</TabsTrigger>
            <TabsTrigger value="3">Codebase Context</TabsTrigger>
          </TabsList>

          <TabsContent value="0" className="mt-0 px-0">
            <GeneralSettingsContent
              allSettings={allSettings}
              prReviewerSettings={prReviewerSettings}
              contentGenerationSettings={contentGenerationSettings}
              automationSettings={automationSettings}
              analysisMetricsSettings={analysisMetricsSettings}
              learningSettings={learningSettings}
              onSettingChange={handleChange}
              isConfigLoaded={configLoaded}
            />
            <LeaderboardPrivacySection
              leaderboardEnabled={leaderboardEnabled}
              loading={leaderboardMutation.isPending}
              onToggle={handleLeaderboardToggle}
            />
            {orgUUID && <ExcludePatterns orgUUID={orgUUID} />}
          </TabsContent>
          <TabsContent value="1" className="mt-0 px-0">
            <div>
              <PRGuidelines />
            </div>
          </TabsContent>
          <TabsContent value="2" className="mt-0 px-0">
            <PRLearnings />
          </TabsContent>
          <TabsContent value="3" className="mt-0 px-0">
            <RepoDependenciesTab />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default function SettingsPage(props: SettingsPageProps) {
  return (
    <Suspense fallback={<div className="animate-pulse h-64" />}>
      <SettingsPageContent {...props} />
    </Suspense>
  );
}
